/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import { fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import {
  API_ENDPOINT,
  API_VERSION,
  API_CLOUD_ACCOUNT_PATH,
  API_FSS_PATH,
} from '@constants/api'
import { genAPIDomain } from '@utils/api'
import { wait } from '@utils/time'

interface CustomFetchArgs extends FetchArgs {
  service?: typeof API_CLOUD_ACCOUNT_PATH | typeof API_FSS_PATH
}

const rawBaseQuery = fetchBaseQuery({
  credentials: 'include',
  baseUrl: '',
  prepareHeaders: (headers, { getState }) => {
    const {
      c1Token: { token },
    } = getState() as Redux.RootState

    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    headers.set('Accept', 'application/json')
    headers.set('api-version', API_VERSION)

    return headers
  },
})

const sharedBaseQuery: BaseQueryFn<
  CustomFetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const { apiDomain, region } = (api.getState() as Redux.RootState).common // don't use apiDomainSelector here for preventing dependency cycle.
  let adjustedUrl = `${apiDomain}${API_ENDPOINT}/${args.url}`

  if (args?.service === API_CLOUD_ACCOUNT_PATH) {
    adjustedUrl = `${genAPIDomain(
      region,
      API_CLOUD_ACCOUNT_PATH
    )}${API_ENDPOINT}/${args.url}`
  }

  const adjustedArgs =
    typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl }
  const result = await rawBaseQuery(adjustedArgs, api, extraOptions)

  window.dispatchEvent(window.C1C_Extend_UserActivity)

  if (result.error && result.error.status === 401) {
    window.dispatchEvent(window.C1C_RefreshC1Token)

    // set a rough delay time for assuring receiving the updated token from C1 Redux.
    await wait(500)

    const retryWithRefreshAuth = await rawBaseQuery(
      adjustedArgs,
      api,
      extraOptions
    )

    return retryWithRefreshAuth
  }

  return result
}

export default retry(sharedBaseQuery, { maxRetries: 5 })
